/**
 * 界面上的短格式显示工具（会话列表时间、模型名）。
 *
 * 为什么单独成模块：这两个函数此前各有**两份逐字相同的副本** ——
 * `AIAssistantPanel/chat-model-pref.ts`（被测）与 `AIAssistantPanel/WritingMode.tsx`（未被测），
 * 而 `formatShortDate` 还有第三份在 `ai-chat/AIChatContext.tsx`（侧边栏在用，也未被测）。
 * 三份实现完全相同却只有一份有测试覆盖 —— 改一处漏一处不会有任何提示。
 * 现收敛到这里作为唯一实现，由 `utils/displayFormat.test.ts` 覆盖。
 */

/**
 * 会话时间 → 相对/绝对混合的短标签。
 *
 * 分档（依次判定）：
 *   < 1 分钟      → 「刚刚」
 *   < 1 小时      → 「N 分钟前」
 *   今天，更早     → 「今天 HH:mm」
 *   更早的日期     → 「M月D日 HH:mm」
 *
 * 注意分档顺序：先按时长，再按"是否同一天"。因此"昨天 23:50 且距今仅 20 分钟"
 * 会显示「20 分钟前」而不是「昨天 23:50」—— 这是刻意的：刚发生的会话，
 * 用户关心的是"多久以前"，而不是它在日历上的归属。
 */
export function formatShortDate(ts: number): string {
  const d = new Date(ts);
  const diff = Date.now() - ts;
  if (diff < 60_000) return '刚刚';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  const timeStr = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (d.toDateString() === new Date().toDateString()) {
    return `今天 ${timeStr}`;
  }
  return `${d.getMonth() + 1}月${d.getDate()}日 ${timeStr}`;
}

/** 模型名缩短：去掉供应商前缀，过长时截断加省略号（列表宽度有限） */
export function shortModelName(model: string): string {
  const last = model.split('/').pop() ?? model;
  return last.length > 30 ? last.slice(0, 28) + '…' : last;
}
